import { createFileRoute, Outlet } from "@tanstack/react-router";
import { SettingsNavigation } from "@/components/settings/settings-navigation";
import { useTranslation } from "react-i18next";
import { Separator } from "@/components/ui/separator";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { getCurrentMemberServer } from "@/server/workspace-members";

interface SettingsLoaderData {
  workspaceId: string;
  isOwner: boolean;
}

export const Route = createFileRoute("/workspace/$workspaceSlug/settings")({
  // Passthrough loader to provide workspaceId to child routes
  // This is necessary because child routes' parentMatchPromise resolves to their
  // direct parent (this settings route), not the workspace layout route.
  loader: async ({ context, parentMatchPromise }): Promise<SettingsLoaderData> => {
    const workspaceId = await getWorkspaceIdWithFallback(context, "settings", parentMatchPromise);
    const currentMember = await getCurrentMemberServer({ data: { workspaceId } });
    return { workspaceId, isOwner: currentMember.role === "workspace_owner" };
  },
  component: SettingsLayout,
});

function SettingsLayout() {
  const { t } = useTranslation(["common"]);
  const { isOwner } = Route.useLoaderData();

  return (
    <div className="flex h-full flex-col bg-white">
      {/* 标题栏 */}
      <div className="h-[64px] px-6">
        <div className="flex h-full items-center">
          <h1 className="text-[16px] font-semibold text-gray-900">{t("user.settings")}</h1>
        </div>
      </div>

      <Separator className="bg-[#dfdfdf]" />

      {/* 二级导航 + 内容区域 */}
      <div className="flex flex-1 overflow-hidden">
        {/* 左侧二级导航 - 固定宽度 180px */}
        <SettingsNavigation isOwner={isOwner} />

        {/* 右侧内容区域 - 弹性宽度 */}
        <div className="flex-1 overflow-auto bg-white">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
